import React from 'react';
import {
  X,
  Clock,
  User,
  Globe,
  Monitor,
  Shield,
  AlertTriangle,
  Activity,
  Terminal,
  FileCode,
  Search,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { useAuditLogDetail } from '../../hooks/useAuditLogs';
import { formatDateTime } from '../../utils/formatters';

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

function JsonBlock({ data, label }) {
  if (!data) return null;

  let parsed = data;
  let displayText = data;

  // Try to parse if it's a JSON string
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
      displayText = JSON.stringify(parsed, null, 2);
    } catch {
      displayText = data;
    }
  } else {
    displayText = JSON.stringify(data, null, 2);
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
        <CopyButton text={displayText} />
      </div>
      <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto font-mono">
        {displayText}
      </pre>
    </div>
  );
}

function DetailRow({ label, value, icon: Icon, highlight = false }) {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
      {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-sm ${highlight ? 'font-medium text-blue-600' : 'text-gray-900'} break-all`}>
          {value}
        </p>
      </div>
    </div>
  );
}

export default function AuditDetailModal({ auditId, onClose }) {
  const { data, isLoading, error } = useAuditLogDetail(auditId);
  const log = data?.data;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Audit Log Details</h2>
              <p className="text-sm text-gray-500">ID: {auditId}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                Error loading audit log: {error.message}
              </div>
            )}

            {log && (
              <div className="space-y-6">
                {/* Summary Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User & Session */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      User & Session
                    </h3>
                    <DetailRow label="User Name" value={log.user_name} />
                    <DetailRow label="Email" value={log.user_email} highlight />
                    <DetailRow label="Role" value={log.user_role} />
                    <DetailRow label="User ID" value={log.user_id} />
                    <DetailRow label="Session ID" value={log.session_id} />
                  </div>

                  {/* Request Details */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Request Details
                    </h3>
                    <DetailRow label="IP Address" value={log.ip_address} icon={Globe} />
                    <DetailRow label="Timestamp" value={formatDateTime(log.created_at)} icon={Clock} />
                    <DetailRow label="HTTP Method" value={log.http_method} />
                    <DetailRow label="Endpoint" value={log.endpoint} highlight />
                    <DetailRow label="Response Status" value={log.response_status} />
                    <DetailRow label="Response Time" value={log.response_time_ms ? `${log.response_time_ms}ms` : null} />
                  </div>
                </div>

                {/* Action Details */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Action Details
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Action Type</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 mt-1">
                        {log.action_type}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Category</p>
                      <p className="text-sm text-gray-900">{log.action_category || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Screen</p>
                      <p className="text-sm text-gray-900">{log.screen_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Records Accessed</p>
                      <p className="text-sm text-gray-900">{log.records_accessed || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Data Access Flags */}
                {(log.accessed_phi || log.accessed_pii) && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Data Access Flags
                    </h3>
                    <div className="flex flex-wrap gap-4">
                      {log.accessed_phi && (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <Shield className="w-3 h-3 mr-1" />
                            PHI Accessed
                          </span>
                          {log.phi_fields && (
                            <span className="text-xs text-gray-600">Fields: {log.phi_fields}</span>
                          )}
                        </div>
                      )}
                      {log.accessed_pii && (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            PII Accessed
                          </span>
                          {log.pii_fields && (
                            <span className="text-xs text-gray-600">Fields: {log.pii_fields}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Resource Info */}
                {(log.resource_type || log.query_type || log.menu_path) && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      Resource Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <DetailRow label="Resource Type" value={log.resource_type} />
                      <DetailRow label="Resource ID" value={log.resource_id} />
                      <DetailRow label="Query Type" value={log.query_type} />
                      <DetailRow label="Menu Path" value={log.menu_path} />
                    </div>
                  </div>
                )}

                {/* Search Criteria */}
                {log.search_criteria && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Search Criteria
                    </h3>
                    <div className="bg-white rounded border border-purple-200 p-3">
                      <code className="text-sm text-purple-700 break-all">{log.search_criteria}</code>
                    </div>
                  </div>
                )}

                {/* Query Parameters */}
                <JsonBlock data={log.query_params} label="Query Parameters" />

                {/* Request Body */}
                <JsonBlock data={log.request_body} label="Request Body" />

                {/* Additional Data */}
                <JsonBlock data={log.additional_data} label="Additional Data" />

                {/* Error Message */}
                {log.error_message && (
                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-red-700 mb-2">Error Message</h3>
                    <pre className="text-sm text-red-600 whitespace-pre-wrap">{log.error_message}</pre>
                  </div>
                )}

                {/* User Agent */}
                {log.user_agent && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Terminal className="w-4 h-4" />
                      User Agent
                    </h3>
                    <p className="text-xs text-gray-600 break-all font-mono">{log.user_agent}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
